// /src/pages/About.js
//
// V3 About — single screen, no document scrolling.
//
// Layout principles:
//   - The whole content row is vertically centered inside the available height
//     (between the 72px header and the footer rendered by AboutShell).
//   - Horizontally aligned with Work and /for/: same max width (1440px) and
//     page gutters as HomePage / PrivateForPage so the site feels one system.
//   - Body copy stays max ~60ch for readability; portrait + doings use the
//     wider band without a narrow capsule.
//   - Portrait stays 280px square and aligns with the top of the heading.
//   - The right column has a 60ch reading width for the paragraph and a
//     wider band for the doings list so labels + details don't jam together.
//   - If a viewport is unusually short, the inner column scrolls — we never
//     trigger document scroll on this route.

import React from "react";

const CV_URL = "/General/Shareef Jasim _ Resume.pdf";
const EMAIL_URL = "mailto:shareefjasim1@gmail.com";

// --- Portrait corner tick -------------------------------------------------
// Small registration-style square poking out of the top-left corner of the
// portrait. Tune these three numbers to taste:
//   TICK_SIZE_PX   — edge length of the square (try 6–10).
//   TICK_OFFSET_PX — how far the square sits relative to the portrait corner.
//                    Negative values pull it OUTWARD (it pokes past the edge);
//                    positive values inset it INSIDE the portrait.
//                    e.g. -4 = pokes out by 4px,  6 = inset 6px from corner.
//   TICK_VISIBLE   — set to false to hide it entirely.
const TICK_SIZE_PX =24;
const TICK_OFFSET_PX = -4;
const TICK_VISIBLE = false;

const AboutPage = () => {
  return (
    <main
      className="
        flex flex-1 flex-col min-h-0 w-full
        bg-white dark:bg-black
        pt-[72px] box-border
        transition-colors duration-500
      "
    >
      {/* Vertical centering wrapper. min-h-full + flex items-center keeps the
          row centred when content fits, falls back to scroll if it doesn't. */}
      <div className="flex-1 min-h-0 overflow-y-auto overscroll-y-contain">
        <div className="min-h-full flex items-center">
          <div
            className="
              w-full max-w-[1440px] mx-auto
              px-6 lg:px-12
              py-8 lg:py-10
            "
          >
            <div
              className="
                flex flex-col lg:flex-row
                items-start
                gap-8 lg:gap-14
              "
            >
              <Portrait />
              <Content />
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

/** 280px square portrait + small corner registration mark. */
const Portrait = () => (
  <aside className="shrink-0 mx-auto lg:mx-0">
    {/* Outer wrapper is `relative` (not the image box) so the tick can sit
        OUTSIDE the portrait without being clipped by overflow-hidden. */}
    <div className="relative inline-block">
      {TICK_VISIBLE && (
        <span
          aria-hidden="true"
          className="absolute z-[1] bg-black dark:bg-white"
          style={{
            top: `${TICK_OFFSET_PX}px`,
            left: `${TICK_OFFSET_PX}px`,
            width: `${TICK_SIZE_PX}px`,
            height: `${TICK_SIZE_PX}px`,
          }}
        />
      )}
      <div
        className="
          relative
          w-[200px] h-[200px] sm:w-[240px] sm:h-[240px] lg:w-[280px] lg:h-[280px]
          overflow-hidden
          bg-neutral-100 dark:bg-neutral-900
        "
      >
        <img
          src="/General/Profile Picture 2024_Square.jpg"
          alt="Portrait of Shareef Jasim"
          className="absolute inset-0 h-full w-full object-cover"
        />
      </div>
    </div>
  </aside>
);

const Content = () => (
  <section className="flex-1 min-w-0 w-full">
    <header>
      <h1
        className="
          text-[1.75rem] sm:text-[1.875rem] lg:text-[2.125rem]
          leading-[1.15] tracking-[-0.01em] font-normal
        "
      >
        <span className="text-black dark:text-white">Hi, I&rsquo;m Shareef,</span>{" "}
        <span className="text-content-muted dark:text-neutral-500 font-light">
          a computational designer &amp; creative technologist.
        </span>
      </h1>
    </header>

    <p
      className="
        mt-5 lg:mt-6
        max-w-[60ch]
        text-[14px] lg:text-[15px] leading-[1.65]
        text-content-secondary dark:text-neutral-400
      "
    >
      I build research-driven software solutions and hardware products at the
      intersection of computational design, digital fabrication, physical
      computing, and software. I turn complex research and real-world constraints
      into functional prototypes, tools, and workflows.
    </p>

    <div className="mt-6 lg:mt-7 flex flex-wrap items-center gap-3">
      <a
        href={CV_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-cursor="pointer"
        className="
          clickable cursor-none
          inline-flex items-center justify-between gap-3
          px-4 py-[10px]
          bg-black text-white dark:bg-white dark:text-black
          font-mono text-[11px] tracking-[0.18em] uppercase
          border border-black dark:border-white
          hover:opacity-85 transition-opacity duration-150
        "
      >
        <span>Download CV</span>
        <span aria-hidden="true">&darr;</span>
      </a>
      <a
        href={EMAIL_URL}
        data-cursor="pointer"
        className="
          clickable cursor-none
          inline-flex items-center justify-between gap-3
          px-4 py-[10px]
          border border-black dark:border-white
          font-mono text-[11px] tracking-[0.18em] uppercase
          text-black dark:text-white
          transition-colors duration-150
          hover:bg-black hover:text-white
          dark:hover:bg-white dark:hover:text-black
        "
      >
        <span>Email me</span>
        <span aria-hidden="true">&#x2197;</span>
      </a>
    </div>

    <DoingsList />
  </section>
);

const DOINGS = [
  {
    label: "Jun 2026 \u2013 Present",
    title: "Research Scientist",
    detail: "Programmable Matter Lab \u00b7 University of Washington",
  },
  {
    label: "Oct 2025 \u2013 Jun 2026",
    title: "Graduate Researcher",
    detail: "Programmable Matter Lab \u00b7 University of Washington",
  },
  {
    label: "Sep 2025 \u2013 Mar 2026",
    title: "Capstone Research Lead",
    detail: "University of Washington \u00b7 sponsored by Microsoft Research",
  },
  {
    label: "Dec 2024 \u2013 Mar 2026",
    title: "Technical Assistant",
    detail: "Global Innovation Exchange Lab \u00b7 University of Washington",
  },
  {
    label: "Sep 2024 \u2013 Mar 2026",
    title: "Master of Science in Technology Innovation",
    detail: "University of Washington",
  },
  {
    label: "Aug 2024 \u2013 Feb 2025",
    title: "Computational Design Consultant",
    detail: "LocAI \u2013 AI71",
  },
  {
    label: "Feb 2023 \u2013 Dec 2024",
    title: "Computational Designer",
    detail: "Imagine Computation GmbH",
  },
  {
    label: "Jul 2021 \u2013 Dec 2023",
    title: "Product Development Manager",
    detail: "Family business \u00b7 Watches House",
  },
  {
    label: "Jun 2020 \u2013 May 2021",
    title: "Computational Designer",
    detail: "GenX Design and Technology",
  },
  {
    label: "Jun 2018 \u2013 Sep 2018",
    title: "Architectural Designer Intern",
    detail: "U+A",
  },
  {
    label: "Sep 2016 \u2013 Jun 2020",
    title: "Bachelor of Architecture",
    detail: "Bilkent University",
  },
];

const DoingsList = () => (
  <dl
    className="
      mt-6 lg:mt-7
      grid grid-cols-1 sm:grid-cols-[max-content_minmax(0,1fr)]
      gap-x-4 lg:gap-x-8
      gap-y-1.5
    "
  >
    {DOINGS.map((item, i) => (
      <React.Fragment key={`${item.label}-${i}`}>
        <dt className="font-mono text-[9px] lg:text-[10px] tracking-[0.12em] uppercase text-content-muted whitespace-nowrap pt-0 sm:pt-[5px]">
          {item.label}
        </dt>
        <dd className="min-w-0 mb-2 sm:mb-0 text-[14px] lg:text-[15px] leading-[1.45]">
          <span className="text-black dark:text-white font-normal">
            {item.title}
          </span>
          {item.detail && (
            <>
              <span className="text-content-muted dark:text-neutral-500">
                {" "}
                &middot;{" "}
              </span>
              <span className="text-content-secondary dark:text-neutral-400 font-light">
                {item.detail}
              </span>
            </>
          )}
        </dd>
      </React.Fragment>
    ))}
  </dl>
);

export default AboutPage;
